import SearchBar from 'components/SearchBar/SearchBar';
import { usePerms } from 'contexts/PermsContext';
import type { WidgetData } from 'features/dashboard/types/widgetTypes';
import useSearch, { useSearchUpdate } from 'hooks/useSearch';
import React, { useEffect, useMemo, useState } from 'react';
import { getEnableLinked, getIndex, getLinked } from '../../../types/guards';
import type { PermItem, RoleFormData } from '../../../types/Roles.types';
import { DEFAULT_INTEGRATIONS, buildPermGroups } from '../../../utils/groupPerms';
import PermissionsDisplay, { collectHeaderKeys } from './components/PermissionsDisplay';

interface PermissionsTabProps {
  formData: RoleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RoleFormData>>;
  permList?: PermItem[];
  edit?: boolean;
  roleid?: number | string;
  widgetData?: WidgetData;
  checks?: Record<string, boolean>;
}

const PermissionsTab: React.FC<PermissionsTabProps> = ({
  formData,
  setFormData,
  permList = [],
  edit = true,
  roleid = 0,
}) => {
  const perms = usePerms();

  const search = useSearch();
  const [, updateSearch] = useSearchUpdate() as [string, (s: string) => void];

  const enableLinkToggle = (array: boolean[], index: number): boolean[] => {
    const temp = [...array];

    permList
      .filter((item) => getEnableLinked(item) > 0)
      .forEach((item) => {
        const idx = getIndex(item) ?? -1;
        const enable = getEnableLinked(item);
        if (idx >= 0 && idx < temp.length && index > idx && index <= idx + enable) temp[idx] = true;
      });

    return temp;
  };

  const { visibleIndices, visiblePermList } = useMemo(() => {
    const userPerms: Record<string, boolean> = perms.get() ?? {};
    const normalizeName = (name: string) =>
      name.replace(/\s/g, '').replace(/\//g, '-').replace(/:/g, '_');
    const indices = new Set<number>();
    const list = (permList || []).filter((filter: PermItem, index: number) => {
      let visible = false;
      const fLinked = getLinked(filter);
      if (fLinked === 0) {
        visible = normalizeName(filter.name) in userPerms;
      } else {
        for (let i = 1; i <= fLinked; i++) {
          const linkedPerm = permList[index + i];
          if (!linkedPerm) continue;
          if (normalizeName(linkedPerm.name) in userPerms) {
            visible = true;
            break;
          }
        }
      }
      if (visible) indices.add(index);
      return visible;
    });
    return { visibleIndices: indices, visiblePermList: list };
  }, [permList, perms]);

  useEffect(() => {
    if (!permList?.length || !formData.permissions?.length) return;

    let changed = false;
    const nextPermissions = [...formData.permissions];

    permList.forEach((item: PermItem) => {
      const linked = getLinked(item);
      if (linked <= 0) return;
      const idx = getIndex(item);
      if (idx === undefined) return;
      let visibleLinkedCount = 0;
      let linkEnabled = true;

      for (let i = 0; i < linked; i++) {
        const linkedIndex = idx + 1 + i;
        if (linkedIndex >= nextPermissions.length) continue;
        if (!visibleIndices.has(linkedIndex)) continue;
        visibleLinkedCount += 1;
        if (!nextPermissions[linkedIndex]) linkEnabled = false;
      }

      if (visibleLinkedCount > 0 && linkEnabled !== nextPermissions[idx]) {
        nextPermissions[idx] = linkEnabled;
        changed = true;
      }
    });

    if (changed) {
      setFormData((prev: RoleFormData) => ({
        ...prev,
        permissions: nextPermissions,
      }));
    }
  }, [permList, formData.permissions, setFormData, visibleIndices]);

  // "Check All" acts on every permission the role is allowed to have (all
  // visible indices), regardless of the current search filter.
  const visibleIndexList = useMemo(() => Array.from(visibleIndices), [visibleIndices]);
  const allVisibleChecked =
    visibleIndexList.length > 0 && visibleIndexList.every((i) => formData.permissions[i]);

  // Count enabled permissions (total true flags in `formData.permissions`).
  const visibleCheckedCount = useMemo(() => {
    const perms = formData.permissions ?? [];
    return perms.reduce((count, p) => count + (p ? 1 : 0), 0);
  }, [formData.permissions]);

  // Grouping: use curated integration grouping util, then fall back to index-based grouping.
  const groups = useMemo(
    () => buildPermGroups(visiblePermList, DEFAULT_INTEGRATIONS),
    [visiblePermList],
  );

  // Map header.index -> child indices for promoted headers (non-contiguous groups)
  const headerChildrenMap = useMemo(() => {
    const map = new Map<number, number[]>();
    groups.forEach((g) => {
      if (g.header && Array.isArray(g.childIndices) && g.childIndices.length > 0) {
        const headerIdx = getIndex(g.header);
        if (typeof headerIdx === 'number') map.set(headerIdx, g.childIndices);
      }
    });
    return map;
  }, [groups]);

  // Lightweight helpers to reduce duplication in toggle logic
  const numericRoleId = parseInt(String(roleid));
  const isRoleReadOnly = numericRoleId === -1 || numericRoleId === -3;

  const getChildStats = (indices: number[]) => {
    let linkEnabled = true;
    let visibleLinkedCount = 0;
    let anyChildEnabled = false;
    const permsArr = formData.permissions ?? [];
    for (const linkedIndex of indices) {
      if (linkedIndex >= permsArr.length) continue;
      if (!visibleIndices.has(linkedIndex)) continue;
      visibleLinkedCount += 1;
      if (!permsArr[linkedIndex]) linkEnabled = false;
      if (permsArr[linkedIndex]) anyChildEnabled = true;
    }
    return { linkEnabled, visibleLinkedCount, anyChildEnabled };
  };

  const applyTargetToIndices = (temp: boolean[], indices: number[], target: boolean) => {
    for (const i of indices) {
      if (i >= 0 && i < temp.length) temp[i] = target;
    }
  };

  // Disable the contiguous `enableLinked` slots that follow a header index.
  const clearEnableLinked = (temp: boolean[], idx: number) => {
    const enableLinkedAtIdx = getEnableLinked(permList[idx]);
    for (let x = 1; x <= enableLinkedAtIdx; x++) {
      const linkedIndex = idx + x;
      if (linkedIndex < temp.length) temp[linkedIndex] = false;
    }
  };

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const collapsibleKeys = useMemo(() => collectHeaderKeys(groups), [groups]);
  const allCollapsed =
    collapsibleKeys.length > 0 && collapsibleKeys.every((key) => collapsedGroups[key]);

  const handleToggleCollapseAll = () => {
    const nextCollapsed = !allCollapsed;
    setCollapsedGroups(() => {
      const next: Record<string, boolean> = {};
      collapsibleKeys.forEach((key) => {
        next[key] = nextCollapsed;
      });
      return next;
    });
  };

  // Initialize nested subgroup keys to collapsed by default on first render
  // (so subsections start closed while top-level sections remain open).
  useEffect(() => {
    if (!groups || groups.length === 0) return;
    if (Object.keys(collapsedGroups).length > 0) return; // already initialized

    const topLevelKeys = groups.filter((g) => g.header).map((g) => g.key);
    const allKeys = collectHeaderKeys(groups);
    const nestedKeys = allKeys.filter((k) => !topLevelKeys.includes(k));
    if (nestedKeys.length === 0) return;

    const initial: Record<string, boolean> = {};
    // Mark nested (subgroup) keys collapsed; leave top-level keys undefined
    nestedKeys.forEach((k) => {
      initial[k] = true;
    });
    setCollapsedGroups(initial);
  }, [groups]);

  const handleToggleAll = () => {
    if (!edit) return;
    const nextValue = !allVisibleChecked;
    setFormData((prev: RoleFormData) => {
      const temp = [...prev.permissions];
      visibleIndices.forEach((i) => {
        if (i >= 0 && i < temp.length) temp[i] = nextValue;
      });
      return { ...prev, permissions: temp };
    });
  };

  // Shared toggle math for a permission item — identical to the original flat
  // list. Reused by both header and child rows.
  const getToggleState = (item: PermItem): { isOff: boolean; onToggle: () => void } => {
    const idx = getIndex(item) ?? -1;
    const linked = getLinked(item);

    // Support curated-group headers by using the computed `headerChildrenMap`
    // (header index -> explicit child indices). This avoids mutating PermItem
    // objects and centralizes grouping in the util.
    const groupChildIndices = headerChildrenMap.get(idx) ?? undefined;

    if (groupChildIndices && groupChildIndices.length > 0) {
      const { linkEnabled, visibleLinkedCount, anyChildEnabled } = getChildStats(groupChildIndices);

      const isOff = isRoleReadOnly
        ? false
        : visibleLinkedCount > 0
          ? !linkEnabled
          : !anyChildEnabled;

      const onToggle = () => {
        if (!edit) return;
        setFormData((prev: RoleFormData) => {
          let temp = [...prev.permissions];

          // If header maps to a real permission slot, use its inverse;
          // otherwise invert the collective child state.
          const headerIdxValid = idx >= 0 && idx < temp.length;
          const target = headerIdxValid ? !temp[idx] : !groupChildIndices.every((i) => temp[i]);

          if (headerIdxValid) temp[idx] = target;
          applyTargetToIndices(temp, groupChildIndices, target);

          // Honor existing `enableLinked` semantics for downstream contiguous
          // slots when enabling a real header permission.
          if (headerIdxValid && target) temp = [...enableLinkToggle(temp, idx)];

          return { ...prev, permissions: temp };
        });
      };

      return { isOff, onToggle };
    }

    // Fallback to original contiguous-linked behavior
    const linkedIndices = Array.from({ length: linked }, (_, i) => idx + 1 + i);
    const { linkEnabled, visibleLinkedCount } = getChildStats(linkedIndices);

    const isOff = isRoleReadOnly
      ? false
      : linked > 0 && visibleLinkedCount > 0
        ? !linkEnabled
        : !(idx >= 0 && idx < formData.permissions.length) || !formData.permissions[idx];

    const onToggle = () => {
      if (!edit) return;
      setFormData((prev: RoleFormData) => {
        let temp = [...prev.permissions];
        if (idx >= 0 && idx < temp.length) temp[idx] = !temp[idx];

        if (idx >= 0 && temp[idx]) temp = [...enableLinkToggle(temp, idx)];
        else if (idx >= 0) clearEnableLinked(temp, idx);

        // Mirror the header state onto its contiguous linked children.
        if (idx >= 0) applyTargetToIndices(temp, linkedIndices, temp[idx]);

        return { ...prev, permissions: temp };
      });
    };

    return { isOff, onToggle };
  };

  return (
    <div className="w-100 h-100 d-flex flex-column overflow-hidden" style={{ minHeight: 0 }}>
      <div className="d-flex flex-row align-items-center justify-content-between px-3 pt-3 pb-2 gap-2 flex-shrink-0 flex-wrap">
        <div className="h4 fw-bolder m-0">
          <span>Permissions</span>
          {visibleCheckedCount > 0 && (
            <div className="mt-1 d-flex align-items-center">
              <span className="text-nowrap" style={{ fontSize: '0.875rem' }}>
                Permissions enabled:
              </span>
              <span
                className="badge ms-1"
                style={{
                  fontSize: '0.875rem',
                  lineHeight: 1,
                  backgroundColor: 'var(--color-success-button)',
                  color: 'var(--color-primary-highlight-foreground)',
                }}
              >
                {visibleCheckedCount}
              </span>
            </div>
          )}
        </div>

        <div className="d-flex flex-row align-items-center gap-2 flex-wrap">
          {collapsibleKeys.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-light btn-sm text-nowrap"
              onClick={handleToggleCollapseAll}
            >
              {allCollapsed ? 'Expand All' : 'Collapse All'}
            </button>
          )}
          {edit && (
            <button
              type="button"
              className="btn btn-outline-light btn-sm text-nowrap"
              onClick={handleToggleAll}
              disabled={visibleIndexList.length === 0}
            >
              {allVisibleChecked ? 'Uncheck All' : 'Check All'}
            </button>
          )}
          <SearchBar
            className=""
            placeholder="Search..."
            inputClassName="text-white bg-primary-shadow rounded-3"
            value={search}
            onChange={(v: string) => updateSearch(v)}
            disabled={false}
          />
        </div>
      </div>
      <div className="border-bottom border-2 border-light-subtle opacity-50 mx-3" />
      <PermissionsDisplay
        groups={groups}
        search={search}
        collapsedGroups={collapsedGroups}
        setCollapsedGroups={setCollapsedGroups}
        getToggleState={getToggleState}
      />
    </div>
  );
};

export default PermissionsTab;
